import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
// v0.18.98 (辉哥 10/25 新口径) — 「scheduler 扩展中所有下拉框 (除搜索下拉框) 都用 flarum 原生的 dropdown」。
//   ⇒ 「卡种模板」从 `<select>` 换成 Flarum 原生 `Dropdown` (⛔ 不是手搓的下拉)。
//   ⚠️ Flarum 的 `Dropdown` 手机端会自动变**底部抽屉** (fixed / 70vh / 上滑 / backdrop),
//      还自带键盘导航、点外关闭、越界翻转 —— 手搓这些一个都没有。
//   ⛔ 用**基础** `Dropdown` (⛔ 不是 `SelectDropdown`): 本扩展既有 33 处全是基础 Dropdown,
//      `SelectDropdown` 的 caret 是 `fas fa-sort` (排序图标) ⇒ 同页两种长相 = 违反「全站一致」。
import Dropdown from 'flarum/common/components/Dropdown';
import Api from '../utils/Api';
// v0.18.94 (辉哥 10/25 14:36 拍板) — 「默认过期时间 = 今天 + 1 年」公用算法
//   (跟「转学员办卡」modal 共用一份, ⛔ 不抄第二遍; 2/29 边界在那边处理)。
import { defaultExpiresAt } from '../utils/DefaultExpiry';
// v0.18.98 (辉哥 10/25 新口径) — 「绑卡 modal 里面, 经办人要用之前做过的搜索下拉框」。
//   ⇒ 用**本仓已有的**「可搜索用户单选」公用组件 (v0.16.7 抽出来的),
//      ⛔ 不许再造第二套 (组件自己有注释写明: 新增同类下拉必须复用, 否则下一轮回归要测两遍)。
//   照抄「转学员办卡」modal (ConvertStudentModal.js, v0.18.84 同一口径) 的用法, 逐字同款。
import SearchableUserPicker from '../utils/SearchableUserPicker';
// v0.19.22 — 后端错误文案 / 当前时间的**共用入口** (与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';
import { nowFormatted } from '../utils/NowFormatted';

/**
 * v0.19.30 — 就地错误条的样式**全部由 LESS 提供** (⛔ 不再用内联样式)。
 *
 * ⚠️ **为什么**: `less/forum.less` 里既有的 `.ZS-tf-hint` / `.ZS-tf-hint.danger`
 *   规则是**嵌在 `.ZS-BookingModal` 作用域里**的 (forum.less L12788 附近)
 *   ⇒ 在本 modal (`.ZS-BindCardModal`) 里**一条都命不中** ⇒ 光加 class 会是灰字。
 * ⇒ v0.19.30 在 `less/forum.less` **末尾**追加了**作用域限定到本批两个 modal** 的
 *   同款声明 (`.ZS-AddStudentModal .ZS-tf-hint` / `.ZS-BindCardModal .ZS-tf-hint`)
 *   ⇒ 本文件**不写任何内联 style**。
 * ⚠️ class 名**照用** `.ZS-tf-hint danger` (**一字不改**) ⇒ 样式由 LESS 提供。
 */

/**
 * BindCardModal — 绑定卡 modal (v0.6.3 重设计, 辉哥 9/25 14:35 + 15:16 拍板)
 *
 * 模式:
 *   - 'create': 给学员绑新卡 (v0.6.3 完全重写)
 *   - 'extend': 延期已存在的卡 (v0.6.2 稳定, 不动)
 *
 * v0.6.3 改动 (create mode):
 *   1. 🏫 当前校区 (自动归属 active_school_id, 锁定, 信息条展示)
 *   2. 🎫 卡种模板 (select)
 *   3. 💵 实收金额 (input + 内嵌应收 ¥XXX + 优惠提示 + 校区隔离 hint)
 *   4. ✏️ 实际售出 + 🎁 赠送 (同 row 2 列, 内嵌 总 N)
 *   5. 📊 课单价 (自动算 = 实收 ÷ 总课时, 仅展示)
 *   6. 🎫 立即开卡 (Switch toggle, 关闭 → 卡未激活)
 *   7. 📅 过期时间 (flatpickr, 开卡时条件必填)
 *   8. 👤 经办人 (业绩分成) — v0.18.98 起 = **搜索下拉框** (单选, 见下)
 *
 * v0.18.98 (辉哥 10/25 新口径) — 两处控件形态改造:
 *   ① 「卡种模板」`<select>` → **Flarum 原生 `Dropdown`** (基础 Dropdown, ⛔ 不是 SelectDropdown)
 *   ② 「经办人」Checkbox 多选列表 → **本仓已有的 `SearchableUserPicker`** (可搜索用户单选)
 *
 * ## ② 为什么「经办人」是**单选**而不是多选 (口径写死, 别自己改成多选)
 *   `agents` 数组在系统里「只存不算」:
 *     · `StudentCardFactory` 只做**一致性校验** (agent_id == agents[0].user_id);
 *     · 业绩 / 提成 / 确收 / 报表口径**全部**取**单值** `agent_id` + `agent_commission_rate`;
 *     · 「转学员建卡」服务端自组 `agents = [{user_id: agent_id, role: agent_type}]` (**单元素**)。
 *   ⇒ 除 `agents[0]` 之外的第 2 个及以后的经办人**没有任何计算用途**。
 *   ⇒ 组件契约上 `SearchableUserPicker` 本来就是**单选**组件 (改共享组件的状态机会波及另外 5 个调用方)。
 *   ⇒ 本批口径 = **单选**; 提交时 `agents` 仍传**数组**, 但只有一条 (后端两条校验天然满足)。
 *
 * ## ② 后端**零改动**
 *   `agent_id` / `agent_type` = 选中的那一个人 ⇒ **业绩归属不变**;
 *   `agents` = 单元素数组 ⇒ `agent_id === agents[0].user_id` / `agent_type === agents[0].role` 天然成立。
 *   ⛔ `src/**` 一个字都不许改。
 *
 * 设计图:
 *   - mobile: /Users/zhaozihui/.minimax/workspace/students-mobile-designs/option-A-bindcard-agents-multi-mobile.html
 *   - desktop: /Users/zhaozihui/.minimax/workspace/students-mobile-designs/option-A-bindcard-agents-multi-desktop.html
 *
 * 不展示:
 *   - ❌ 本月业绩 / 佣金分账 / 业绩汇总 (DB 保留, 前端不展示)
 *   - ❌ 校区 select (自动全局 active)
 *   - ❌ 佣金比例 input (后续放到别处)
 *   - ❌ 销售/教练组 radio (那是用户组, 不是人)
 */
export default class BindCardModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    const student = vnode.attrs.student;
    const card = vnode.attrs.card;
    const mode = vnode.attrs.mode || 'create';
    this.mode = mode;
    this.student = student;
    this.card = card;
    this.onCloseCb = vnode.attrs.onClose;
    this.schools = vnode.attrs.schools || [];
    this.activeSchoolId = vnode.attrs.activeSchoolId;

    // ── v0.19.30 — 「待建号模式」(第 2 步) ────────────────────────────────
    //   第 1 步 (AddStudentModal) 只填账号信息 + 只读查重 ⇒ **不建号**;
    //   账号**在第 2 步点「绑定卡片」时**才真正创建 (先建号, 再建卡)。
    //
    //   ⚠️ ⛔ **不许**把 pendingStudent 塞进 `this.student`
    //      (那会让 `form.student_id` 变非 null ⇒ 建卡会打到**错的人**身上)。
    //   ⚠️ 没有 pendingStudent ⇒ **现有两种用法行为一字不变**
    //      (`StudentsListTab.openBindCard` / `StudentsListTab.openExtend`)。
    this.pendingStudent = vnode.attrs.pendingStudent || null;
    // 第 2 步建完号 **且** 建完卡后回调第 1 步 (关掉第 1 步 + 刷新列表)
    this.onDoneCb = vnode.attrs.onDone || null;
    // 已经建出来的学员 id (建号成功 ⇒ 建卡失败重试时**跳过建号**, ⛔ 不重复建号)
    this.createdStudentId = null;
    // v0.19.30 补正 — 第 1 步传进来的「建号成功」回调 (建号成功后回传用户名 + id)
    //   ⇒ 第 1 步的查重遇到「已占用」时, 若占用者**正是本流程刚建的**就放行
    //      (否则建卡失败后点「返回上一步」会被卡死在第 1 步, 且留下孤儿账号)
    this.onUserCreatedCb = vnode.attrs.onUserCreated || null;
    // 就地错误条: { type: 'create' (建号失败) | 'card' (建卡失败), detail: string }
    this.bindError = null;
    if (mode === 'extend') {
      // v0.6.2 — extend mode 不动 (辉哥 9/25 12:13 拍板), 保留稳定
      this.form = {
        student_id: student ? student.id : card ? card.student_id : null,
        template_id: card ? card.template_id : null,
        school_id: card ? card.school_id : null,
        expires_at: card ? card.expires_at : null,
        agent_id: card ? card.agent_id : null,
        agent_type: card ? card.agent_type : 'sales',
        agent_commission_rate: card ? card.agent_commission_rate : null
      };
    } else {
      // v0.6.3 — create mode 重设计
      this.form = {
        student_id: student ? student.id : null,
        template_id: null,
        // SOP 88 — school_id 强制 = active_school_id (前端不能传, 后端 ignore)
        school_id: this.activeSchoolId ? +this.activeSchoolId : null,
        // 实收金额
        price: null,
        // 实际售出 + 赠送 = total_lessons
        actual_lessons: null,
        bonus_lessons: null,
        // 立即开卡 + 过期时间
        is_activated: false,
        activated_at: null,
        // v0.18.94 — ⚠️ 这里**仍然是 null** (⛔ 不预填):
        //   create 模式的「立即开卡」开关**初始就是关的** ⇒ 按口径「开关关闭时 ⛔ 不预填」
        //   (沿用绑卡 modal 既有行为: 关闭时 expires_at = null)。
        //   ⇒ 「今天 + 1 年」的预填发生在**打开开关的那一刻** (见 renderCreateMode 的 onchange)。
        expires_at: null,
        // v0.18.98 — 「经办人」改成**搜索下拉框** (单选) ⇒ 状态从 `agents: []` (多选数组)
        //   换成 `agent_id` (选中的那个人的 id, 字符串) + `agent_role` (他的角色)。
        //   ⚠️ `agent_role` 由组件的 `setValue(key, v, item)` 第三参数 (候选项对象) 带出来
        //     —— 候选自带 `role` 字段 (后端 `ListUsersByRoleController::row()` 返的),
        //     ⛔ **原样透传** (coach / sales / manager 都有可能), 不许自己映射。
        agent_id: '',
        agent_role: null,
        // status
        status: 'active'
      };
    }
    this.templates = [];

    // ── v0.18.98 — 「经办人」用**本仓已有的**「可搜索用户单选」公用组件 ──────────
    //   照抄「转学员办卡」modal (ConvertStudentModal.js:189-202, v0.18.84 同一口径) 的用法, 逐字同款:
    //   · 候选来源与改前**完全一致**: GET /users?role=coach,sales&school_id=N
    //     (组件自己拉, ⛔ 本 modal 不再重复拉一遍 —— 改前是本 modal 拉的)。
    //   · `bare: true` —— 宿主(这个弹层)自己已经有「经办人」标签了, 不套组件的外壳,
    //     否则会出现**两个**标签 (组件自己的注释里写明了这条: bare = 只输出 .ZS-agent-search)。
    //   · ⛔ 组件**没有**「默认选中第一个人」这种回落: 选中值对不上候选就是**空**的。
    //   · ⚠️ 组件是**单选**组件 (getValue 返**字符串** id) —— 本批口径就是单选 (理由见文件头注释)。
    this.picker = new SearchableUserPicker({
      host: this,
      getSchoolId: () => this.activeSchoolId,
      getValue: key => this.form[key],
      // v0.18.98 — ⛔ 后端零改动 ⇒ `agents` 仍然是**数组**, 但只放**一个**人:
      //   `agent_id === agents[0].user_id` / `agent_type === agents[0].role` 两条校验天然满足。
      setValue: (key, v, item) => {
        this.form[key] = v;
        this.form.agent_role = item ? item.role : null;
      },
      fields: {
        agent_id: {
          role: 'coach,sales',
          bare: true,
          placeholder: '输入姓名筛选…',
          emptyText: '当前校区下没有匹配的候选人'
        }
      }
    });
    this.loading = true;
    this.saving = false;
    this._flatpickrTimer = null;
    this._flatpickrInstance = null;
    // v0.18.94 — 实例绑在**哪个** input 节点上 (开关一关一开 ⇒ 节点会换 ⇒ 要能认出来)
    this._flatpickrEl = null;
    this.load();
  }
  className() {
    return 'Modal--medium Modal--fullscreen-padded ZS-BindCardModal';
  }
  title() {
    if (this.mode === 'extend') {
      return m("span", null, m("i", {
        className: "fas fa-clock",
        style: "color:var(--zs-warning-f59e0b, #f59e0b);margin-right:8px;"
      }), "\u5EF6\u671F\u5B66\u5458\u5361");
    }
    // v0.19.30 — 待建号模式 (第 2 步): 标题用第 1 步填的昵称/用户名
    //   ⚠️ 现有两种用法的标题**一字不改** (下面那两行是原样保留的)。
    const studentName = this.pendingStudent ? this.pendingStudent.nickname || this.pendingStudent.username : this.student ? this.student.display_name || this.student.username : '学员';
    return m("span", null, m("i", {
      className: "fas fa-ticket",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:8px;"
    }), "\u7ED1\u5B9A\u5361\u7ED9 ", studentName);
  }

  /**
   * v0.19.30 — locale key 兜底。
   *
   * ⚠️ **为什么需要**: Flarum 的 translator 找不到 key 时**原样返回 key 本身**
   *   (core `Translator.tsx`: `fireDebugWarning('Missing translation for key')` → `return id`)
   *   ⇒ 本批新增的 key 由 Task B 补 locale, 在那之前页面上会出现
   *   「ziven-scheduler.forum.student-list.bind-summary-username」这种**裸 key 字符串**。
   * ⇒ 兜底成中文; Task B 补上 key 之后**自动**走 trans() (无需改本文件)。
   *
   * @param {string} key      locale key
   * @param {string} fallback key 缺失时的兜底文案
   * @param {Object} [params] 插值参数 (Flarum 官方 `{placeholder}` 语法)
   */
  t(key, fallback, params) {
    // ⚠️ v0.19.30 补正 — ⛔ 第三个参数 `true` (extract) **必须给**:
    //   Flarum 的 `trans(id, params)` 在消息**含 `{placeholder}`** 时返回的是**数组**
    //   (core `Translator.tsx:136-143` → `formatter.rich()`), 不是字符串;
    //   拿它去拼字符串 ⇒ 数组按 "," 强制转换 ⇒ 文案里用户名两侧多出逗号。
    //   `trans(id, params, true)` ⇒ 返回**字符串** (core 的 `extractText` 用 '' 拼接, 无逗号)。
    const v = app.translator.trans(key, params || {}, true);
    return v === key ? fallback : v;
  }
  async load() {
    try {
      this.loading = true;
      m.redraw();
      this.templates = (await Api.list('card-templates', {
        status: 'enabled'
      })) || [];
      // v0.18.98 — 「经办人」候选**交给组件自己拉** (⛔ 本 modal 不再重复拉一遍 ——
      //   改前是本 modal 拉的 `Api.list('users', { role: 'coach,sales', school_id })`,
      //   现在组件用**同一套口径**拉: role=coach,sales + 当前校区 school_id。
      //   ⚠️ v0.17.38「A 方案」: 候选里**没有** manager —— 那个 role 会让开卡直接 422。
      //   ⚠️ extend 模式**不渲染**经办人 ⇒ 不必拉候选 (少一个请求)。
      if (this.mode === 'create') {
        await this.picker.loadAll();
      }
    } catch (e) {
      console.error('bind card load failed', e);
    } finally {
      this.loading = false;
      m.redraw();
      this.tryInitFlatpickr();
    }
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.tryInitFlatpickr();
    // v0.18.98 — 可搜索用户单选组件的生命周期 (与 ConvertStudentModal / FixedLessonModal 同款)
    this.picker.mount();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.tryInitFlatpickr();
    // v0.18.98 — 每轮 patch 后重新判定下拉要不要向上翻
    this.picker.afterUpdate();
  }
  onremove(vnode) {
    super.onremove(vnode);
    // v0.18.98 — ⛔ **必须**摘 (组件在 document 上挂了 mousedown 监听),
    //   否则 modal 关掉后监听还活着, 每次全站 mousedown 都跑进死实例。
    this.picker.destroy();
    if (this._flatpickrTimer) {
      clearTimeout(this._flatpickrTimer);
      this._flatpickrTimer = null;
    }
    if (this._flatpickrInstance) {
      this._flatpickrInstance.destroy();
      this._flatpickrInstance = null;
    }
    this._flatpickrEl = null;
  }

  // r.1 FAIL MINOR 3: vendor flatpickr 异步加载, 等 ready (跟 ziven-event DefaultGroupSettingsPage L17-21 同款)
  tryInitFlatpickr() {
    const el = this.element ? this.element.querySelector('.ZS-flatpickr-input') : null;
    // ── v0.18.94 (bug fix, 跟「默认 1 年」配套) ────────────────────────────
    //   ⚠️ 开关一关一开 ⇒ 日期框是**新的 DOM 节点** (mithril 整段重建)
    //      ⇒ 旧实例还绑在**已被摘掉**的节点上 ⇒ 新框既没有 flatpickr、也显示不出值
    //      ⇒ 员工就卡死了 (开卡时 expires_at 必填 ⇒ 主按钮永远禁用, 又没法选日期)。
    //   ⇒ 节点换了 / 节点没了 ⇒ 先销毁旧实例, 再挂新的。
    //   ⛔ 只动**挂载**这一条链路, ⛔ 不动表单语义 (必填/预填口径一个字没改)。
    if (this._flatpickrInstance && this._flatpickrEl !== el) {
      try {
        this._flatpickrInstance.destroy();
      } catch (e) {/* noop */}
      this._flatpickrInstance = null;
      this._flatpickrEl = null;
    }
    if (this._flatpickrInstance) return;
    const tryOnce = () => {
      if (typeof window.flatpickr !== 'function') {
        this._flatpickrTimer = setTimeout(tryOnce, 100);
        return;
      }
      const el2 = this.element ? this.element.querySelector('.ZS-flatpickr-input') : null;
      if (!el2) return;
      try {
        this._flatpickrInstance = window.flatpickr(el2, {
          dateFormat: 'Y-m-d',
          minDate: 'today',
          locale: window.flatpickr.l10ns && window.flatpickr.l10ns.zh || 'default',
          onChange: (selectedDates, dateStr) => {
            this.form.expires_at = dateStr;
            m.redraw();
          }
        });
        this._flatpickrEl = el2;
        if (this.form.expires_at) {
          this._flatpickrInstance.setDate(this.form.expires_at, false);
        }
      } catch (e) {
        console.error('flatpickr init failed', e);
      }
    };
    tryOnce();
  }

  // ── 自动算: 课单价 = 实收 ÷ (实际 + 赠送) ───────────────────────────
  calcUnitPrice() {
    const price = parseFloat(this.form.price) || 0;
    const total = (parseInt(this.form.actual_lessons) || 0) + (parseInt(this.form.bonus_lessons) || 0);
    if (total <= 0 || price <= 0) return {
      price: 0,
      total: 0,
      unit: 0
    };
    return {
      price,
      total,
      unit: price / total
    };
  }

  // 当前选中的卡模板 (用于内嵌"应收")
  selectedTemplate() {
    if (!this.form.template_id) return null;
    return (this.templates || []).find(t => Number(t.id) === Number(this.form.template_id)) || null;
  }

  /**
   * v0.18.98 — 卡种下拉里每一项的显示文本 (选项与按钮标题**逐字一致**)。
   *
   * ⚠️ 文案与改前的 `<option>` **一字不改**: `{t.name} ¥{t.price} / {t.lessons}课时`
   *   (改前是 JSX 的多个文本节点, 现在是模板字符串 ⇒ 渲染出来的文本完全相同)。
   */
  templateLabel(t) {
    return `${t.name} ¥${t.price} / ${t.lessons}课时`;
  }

  /**
   * v0.18.98 — 「卡种模板」下拉 = **Flarum 原生 `Dropdown`** (照抄 ConvertStudentModal.js 逐字同款)。
   *
   * ## 三个必须遵守的 core 契约 (踩过坑)
   *   ① 每个 child 必须对应一个 `<li>` —— core CSS 是**直接子选择器** `.Dropdown-menu > li > button`
   *      ⇒ 把多个 Button 塞进一个数组 child 会只包出一个 `<li>`、样式全废。
   *   ② `Dropdown` **没有「当前选中值」概念** —— `label` 是纯 `attrs.label`,
   *      选中态靠 `<Button active>` ⇒ 必须自己算 `current` 和 `label`。
   *   ③ `lazyDraw={true}` **必带** —— 否则 `shown.bs.dropdown` 的异步 `m.redraw()` 会把 menu 的
   *      className 重置回 `Dropdown-menu dropdown-menu ...`, 抹掉 Bootstrap 的 `.show`, menu 又变 `display:none`。
   *
   * ## ⛔ 「必填」字段的口径 (与 B2 的「可选」字段**处理不同**, 不许一刀切)
   *   · 选项里**不放**「清空」项 —— 必填, 选了就不该能取消。
   *   · 占位 label = `— 选择卡种 —` (与改前的 `<option>` 文案**一字不改**)。
   *   · **候选没就绪时整个 Dropdown 不渲染** (外层 `this.loading` 已经兜住: 列表未到齐时
   *     整个表单都不渲染, 只显示「加载中…」) ⇒ ⛔ 不许让 label 在候选没到齐时显示第一项。
   */
  renderTemplateDropdown() {
    const current = this.selectedTemplate();
    const items = this.templates.length ? this.templates.map(t => m(Button, {
      key: t.id,
      active: !!current && Number(current.id) === Number(t.id),
      onclick: () => this.chooseTemplate(t)
    }, this.templateLabel(t))) : [m(Button, {
      key: "__empty",
      disabled: true
    }, "\u6682\u65E0\u53EF\u9009\u9879")];
    return m(Dropdown, {
      className: "ZSC-choice-dropdown",
      buttonClassName: "ZS-form-input",
      menuClassName: "ZSC-choice-dropdown-menu",
      label: current ? this.templateLabel(current) : '— 选择卡种 —',
      lazyDraw: true
    }, items);
  }

  /**
   * v0.18.98 — 选中卡种 (值存 id; ⛔ 不改列表顺序)。
   *
   * ⚠️ 「切换卡种时『实收自动填应收默认值』」的逻辑**保留** (改前是在 `<select>` 的 onchange 里):
   *   实收还没填 (空 / 0) ⇒ 自动填这个卡种的应收价。
   */
  chooseTemplate(t) {
    this.form.template_id = Number(t.id);
    if (this.form.template_id && (!this.form.price || this.form.price === 0)) {
      const newTpl = this.selectedTemplate();
      if (newTpl) this.form.price = parseFloat(newTpl.price) || 0;
    }
    // ⚠️ `m` 是全局 (webpack 解析不到 'mithril' 那个包名) ⇒ 兜底判一下,
    //    写法跟 js/src/forum/utils/SearchableUserPicker.js:151 同款。
    if (typeof m !== 'undefined') m.redraw();
  }

  // 应收金额 (从卡模板价)
  expectedPrice() {
    const tpl = this.selectedTemplate();
    return tpl ? parseFloat(tpl.price) || 0 : 0;
  }

  // 实收 - 应收 = 优惠金额
  discountInfo() {
    const expected = this.expectedPrice();
    const actual = parseFloat(this.form.price) || 0;
    if (!expected || !actual) return null;
    const diff = expected - actual;
    if (diff <= 0) return null; // 实收 >= 应收, 没优惠
    const pct = (diff / expected * 100).toFixed(1);
    return {
      amount: diff,
      pct
    };
  }

  // 当前校区名字 (locked info bar)
  currentSchoolName() {
    const s = (this.schools || []).find(x => String(x.id) === String(this.activeSchoolId));
    return s ? s.name : '默认校区';
  }

  // 提交按钮 disable 判断: 必填项未填 / saving 中
  canSubmit() {
    if (this.saving) return false;
    // v0.19.30 — 待建号模式: `student_id` 必然是 null (账号**还没建**)
    //   ⇒ 必须**跳过** student_id 这一项, 否则主按钮永远禁用。
    if (!this.pendingStudent && !this.form.student_id) return false;
    if (!this.form.template_id) return false;
    // 实收 + 总课时必填
    const total = (parseInt(this.form.actual_lessons) || 0) + (parseInt(this.form.bonus_lessons) || 0);
    if (total <= 0) return false;
    if (!this.form.price || parseFloat(this.form.price) <= 0) return false;
    // 经办人必填 (v0.18.98 — 搜索下拉框是**单选** ⇒ 有值才算选了)
    if (!this.form.agent_id) return false;
    // 开卡时, expires_at 必填
    if (this.form.is_activated && !this.form.expires_at) return false;
    return true;
  }
  async submit() {
    if (this.mode === 'extend' && this.card) {
      // v0.6.2 extend mode — 保持不变
      try {
        if (!this.form.expires_at) {
          alert('请选择新过期日期');
          return;
        }
        await Api.extend('student-cards', this.card.id, {
          expires_at: this.form.expires_at
        });
        this.saving = false;
        if (this.onCloseCb) this.onCloseCb();
        this.hide();
      } catch (e) {
        this.saving = false;
        console.error(e);
        alert(e.message || '保存失败');
      }
      return;
    }

    // v0.6.3 create mode
    try {
      this.saving = true;
      m.redraw();

      // ── v0.19.30 — 待建号模式: **先建号, 再建卡** ──────────────────────
      //   ⛔ 建号 payload **绝对不许带 `template_id`**
      //      (带了会产出「裸卡」: 无经办人 / 无佣金 / 无过期时间)
      //   ⛔ 建号**只走** `Api.create('users', …)` 这**一个**入口
      //      (⛔ 不许在这里再抄一份 `app.request` 拼 URL 的建号代码)
      //   ⛔ `school_id` 由后端强制 = active_school_id (前端不传)
      if (this.pendingStudent && !this.createdStudentId) {
        const p = this.pendingStudent;
        const user = await Api.create('users', {
          username: p.username,
          nickname: p.nickname,
          email: p.email,
          phone: p.phone,
          school_id: this.activeSchoolId
        }, {
          errorHandler: () => {}
        });
        // ⚠️ v0.19.30 — 第三参 `{ errorHandler: () => {} }` = **抑制 Flarum 默认的全局红条**:
        //   Flarum 2.0 的 `app.request` 默认会弹一条全局红色 Alert (core `Application.tsx`
        //   的 `requestErrorCatch`) ⇒ 建号失败时页面上会同时出现「全局红条 + 我们的就地错误条」。
        //   ⚠️ core 契约: errorHandler 返回 `undefined` ⇒ **不弹**全局红条 (之后仍然 reject,
        //     调用方的 catch 照常拿到原始 error); 返回 `false` ⇒ **回落默认** ⇒ **会弹**
        //     ⇒ ⛔ 绝不能返回 false ⇒ 这里写 `() => {}`。
        //   ⚠️ 本处**只在「待建号模式」里**执行 ⇒ 既有两种用法 (给已有学员绑卡 / 延期) 不受影响。
        // ⚠️ 后端返的是**顶层裸对象** `{ok, id, username, display_name, …}`
        //   (CreateStudentController L116-127), 而 `Api.create` → `app.request`
        //   → mithril 的 `extract` = `JSON.parse(responseText)` **不做信封解包**
        //   ⇒ 直接取 `user.id` 就是 user id。
        this.createdStudentId = user && user.id ? Number(user.id) : null;
        if (!this.createdStudentId) throw new Error('建号未返回用户 id');
        // v0.19.30 补正 — 通知**第 1 步**「这个用户名已经建出来了」
        //   ⇒ 第 1 步的查重遇到「已占用」时, 若占用者**正是本流程刚建的**就放行
        //      (否则建卡失败后点「返回上一步」会被卡死在第 1 步, 且留下孤儿账号)
        if (this.onUserCreatedCb) {
          this.onUserCreatedCb({
            username: p.username,
            id: this.createdStudentId
          });
        }
      }
      const total = (parseInt(this.form.actual_lessons) || 0) + (parseInt(this.form.bonus_lessons) || 0);
      // v0.7.8d — 同时把经办人字段 (agent_id / agent_type) 单独发给后端 (ranking 查的是这两个字段, 不是 agents JSON)
      // v0.18.98 — 「经办人」改成**单选**的搜索下拉框 ⇒ 状态从 `agents` 数组换成
      //   `agent_id` (那个人的 id) + `agent_role` (他的角色, 由候选项对象带出来)。
      //   ⛔ **后端零改动** ⇒ `agents` 仍传**数组**, 但只有一条:
      //     后端 `StudentCardFactory` 的两条一致性校验
      //     (agent_id == agents[0].user_id / agent_type == agents[0].role) 天然满足。
      const agentId = this.form.agent_id ? Number(this.form.agent_id) : null;
      const agentType = this.form.agent_role || null;
      const payload = {
        // v0.19.30 — 待建号模式: student_id 用**刚建出来**的那个 id
        //   (其它用法一字不改, 还是 `this.form.student_id`)
        student_id: this.pendingStudent ? this.createdStudentId : this.form.student_id,
        template_id: this.form.template_id,
        // SOP 88 — school_id 由后端强制 = active_school_id, 前端不传
        // (后端 ignore 前端传的 school_id)
        actual_lessons: parseInt(this.form.actual_lessons) || 0,
        bonus_lessons: parseInt(this.form.bonus_lessons) || 0,
        total_lessons: total,
        price: parseFloat(this.form.price) || 0,
        is_activated: !!this.form.is_activated,
        activated_at: this.form.is_activated ? this.form.activated_at || nowFormatted() : null,
        expires_at: this.form.expires_at || null,
        // v0.7.8d — 强制经办人字段 (辉哥 10/3 21:10 拍板 B 方案, 防 ranking 数据空)
        agent_id: agentId,
        agent_type: agentType,
        agent_commission_rate: this.form.agent_commission_rate || null,
        // v0.18.98 — 单元素数组 (⛔ 后端格式一字不改)
        agents: agentId ? [{
          user_id: agentId,
          role: agentType
        }] : [],
        status: this.form.status
      };

      // ⚠️ v0.19.30 — 第三参只在**「待建号模式」**传 `{ errorHandler: () => {} }`
      //   (抑制 Flarum 默认的全局红条 ⇒ 失败由我们的**就地错误条**接管);
      //   既有两种用法 (给已有学员绑卡 / 延期) **不传 opts** ⇒ 保持旧行为 (全局红条照旧)。
      //   ⚠️ 传 `undefined` ⇒ `Api.create` 的默认参数 `opts = {}` 生效 ⇒ 行为与改前逐字相同。
      await Api.create('student-cards', payload, this.pendingStudent ? {
        errorHandler: () => {}
      } : undefined);
      this.saving = false;
      if (this.pendingStudent) {
        // ── v0.19.30 — 待建号模式收尾: 关掉第 2 步 → 通知第 1 步关自己 + 刷新列表 ──
        //   ⛔ 用 `app.modal.close()` 关**栈顶**(= 本 modal), ⛔ **不用** `this.hide()`:
        //     `Modal.hide()` → `ModalManager.animateHide()` ⇒ 置 `modalClosing = true`,
        //     并在**当前栈顶**的 DOM 上挂 transitionend 监听; 这里栈顶**马上**要换人
        //     (第 1 步 modal 顶上来) ⇒ 一旦那个 DOM 被移除, transitionend 永不触发
        //     ⇒ 闩锁永久停在 true ⇒ **全站弹窗从此关不掉**。
        //     (同款血泪警告见 CardDetailModal.js:100-111 / RefundApproveModal.js:313)
        app.modal.close();
        if (this.onDoneCb) this.onDoneCb();
        return;
      }

      // v0.6.3 既有收尾 —— 现有两种用法**一字不改**
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
    } catch (e) {
      this.saving = false;
      m.redraw();
      console.error(e);
      const msg = errReason(e, '保存失败');
      // v0.19.30 — 待建号模式: **就地**错误条 (⛔ 不用 alert)
      if (this.pendingStudent) {
        // 分两类失败:
        //   ① `createdStudentId` 还是 null ⇒ **建号**失败 (用户名/邮箱被占用等)
        //      ⇒ 给「← 返回上一步修改用户名」按钮 (关掉本 modal ⇒ 第 1 步数据一个字没丢)
        //   ② `createdStudentId` 有值 ⇒ 账号**已建**, 是**建卡**失败 (卡种/经办人/课时闸)
        //      ⇒ **不给**「返回上一步」; 再点一次主按钮时 `createdStudentId` 已有值
        //        ⇒ **跳过建号**直接建卡 ⇒ ⛔ 不重复建号
        this.bindError = {
          type: this.createdStudentId ? 'card' : 'create',
          detail: msg
        };
        m.redraw();
        return;
      }
      alert(msg);
    }
  }

  // ── 视图: extend mode 跟 v0.6.2 完全一致 ───────────────────────────
  renderExtendMode() {
    return m("div", null, m("div", {
      className: "Modal-body"
    }, this.loading && m("div", {
      style: "padding:30px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u4E2D..."), !this.loading && m("div", {
      className: "ZS-modal-form"
    }, m("div", {
      className: "ZS-form-row"
    }, m("label", null, m("i", {
      className: "fas fa-calendar-days",
      style: "color:var(--zs-warning-f59e0b, #f59e0b);margin-right:6px;"
    }), "\u8FC7\u671F\u65F6\u95F4"), m("input", {
      className: "ZS-form-input ZS-flatpickr-input",
      placeholder: "\u9009\u62E9\u8FC7\u671F\u65E5\u671F (\u4E0D\u9650\u7559\u7A7A)"
    })))), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: this.saving,
      onclick: () => this.submit()
    }, this.saving ? '保存中...' : '确认延期')));
  }

  /**
   * v0.19.30 — 待建号模式: **账号摘要条** (第 1 步填的账号信息, 放在「当前校区」条**上面**)。
   *
   * ⚠️ **复用现成 class, ⛔ 不新增 LESS**: `.ZSC-source` / `.ZSC-kv` / `.ZSC-kv .k` / `.ZSC-kv .v`
   *   / `.ZSC-source-note` 都是 `less/forum.less` 里**已存在**的全局规则。
   */
  renderPendingSummary() {
    const p = this.pendingStudent;
    if (!p) return null;
    return m("div", {
      className: "ZSC-source"
    }, m("div", {
      className: "ZSC-kv"
    }, m("div", null, m("div", {
      className: "k"
    }, this.t('ziven-scheduler.forum.student-list.bind-summary-username', '用户名')), m("div", {
      className: "v"
    }, p.username)), m("div", null, m("div", {
      className: "k"
    }, this.t('ziven-scheduler.forum.student-list.bind-summary-nickname', '昵称')), m("div", {
      className: "v"
    }, p.nickname || '—')), m("div", null, m("div", {
      className: "k"
    }, this.t('ziven-scheduler.forum.student-list.bind-summary-phone', '手机号')), m("div", {
      className: "v"
    }, p.phone)), m("div", null, m("div", {
      className: "k"
    }, this.t('ziven-scheduler.forum.student-list.bind-summary-email', '邮箱')), m("div", {
      className: "v"
    }, p.email)), m("div", null, m("div", {
      className: "k"
    }, this.t('ziven-scheduler.forum.student-list.bind-summary-school', '校区')), m("div", {
      className: "v"
    }, this.currentSchoolName()))), m("div", {
      className: "ZSC-source-note"
    }, this.t('ziven-scheduler.forum.student-list.bind-summary-note', '以上信息来自第 1 步。学员账号将在本步创建 —— 点「绑定卡片」后才真正建号。')));
  }

  /**
   * v0.19.30 — A 态文案: 「创建学员账号失败：{后端返回的原文}」
   *
   * ⚠️ locale 值带不带冒号都能出「…失败：原文」这个形状 (Task B 当前的
   *   `bind-err-create` 值是「创建学员账号失败」, **不带**冒号)。
   */
  createErrorText(err) {
    const phrase = this.t('ziven-scheduler.forum.student-list.bind-err-create', '创建学员账号失败');
    const head = /[:：]\s*$/.test(phrase) ? phrase : phrase + '：';
    const detail = err.detail || this.t('ziven-scheduler.forum.student-list.bind-err-create-detail', '请检查用户名、邮箱是否已被占用');
    return head + detail;
  }

  /**
   * v0.19.30 — B 态文案: 「学员账号已创建（{username}），但卡片未绑定成功。」
   *            + 「修正下方卡信息后再点一次「绑定卡片」即可 —— 不会重复建号。」
   *
   * ⚠️ locale 值里若**没有** `{username}` 占位 (trans() 就插不进去) ⇒ 这里补一个括号用户名,
   *   保证卡片 §2.3 / §4.6 要求的「学员账号已创建（{username}），…」永远带得上用户名。
   *   ⚠️ 中文 locale 走「插在『账号已创建』后面」⇒ 与卡片文案**逐字一致**;
   *   其它语种 (locale 里没有『账号已创建』这几个字) 退化成「句尾追加（用户名）」。
   */
  cardErrorText(username) {
    const phrase = this.t('ziven-scheduler.forum.student-list.bind-err-created', `学员账号已创建（${username}），但卡片未绑定成功。`, {
      username
    });
    let head = phrase;
    if (head.indexOf(username) < 0) {
      const replaced = head.replace('账号已创建', '账号已创建（' + username + '）');
      head = replaced !== head ? replaced : head + '（' + username + '）';
    }
    const tail = this.t('ziven-scheduler.forum.student-list.bind-err-created-detail', '修正下方卡信息后再点一次「绑定卡片」即可 —— 不会重复建号。');
    return head + ' ' + tail;
  }

  /**
   * v0.19.30 — 待建号模式: **就地错误条** (放在摘要条**上面**)。
   *
   * ⚠️ 样式: `.ZS-tf-hint` + `.ZS-tf-hint.danger` (**class 名一字不改**)
   *   ⇒ 由 `less/forum.less` **末尾**追加的 `.ZS-BindCardModal .ZS-tf-hint` 提供
   *     (⛔ 本 modal **不再写任何内联 style**; 既有那两条规则嵌在 `.ZS-BookingModal`
   *     作用域里 ⇒ 在本 modal 命不中 ⇒ 才需要末尾那段作用域限定的同款声明)。
   *
   * ⚠️ 两种失败态:
   *   ① `type: 'create'` — 建号失败 ⇒ 附「← 返回上一步修改用户名」按钮
   *      (onclick = `app.modal.close()` ⇒ 关栈顶 = 关掉本 modal ⇒ 第 1 步还在栈里, 数据没丢)
   *   ② `type: 'card'` — 建卡失败 (账号已建) ⇒ **不给**「返回上一步」, ⛔ 也不重复建号
   */
  renderBindError() {
    const err = this.bindError;
    if (!err || !this.pendingStudent) return null;
    const isCreate = err.type === 'create';
    const username = this.pendingStudent.username;
    return m("div", {
      className: "ZS-tf-hint danger"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      "aria-hidden": "true"
    }), m("span", null, isCreate ? this.createErrorText(err) : this.cardErrorText(username)), isCreate && m("button", {
      className: "ZSC-chip",
      style: {
        cursor: 'pointer'
      },
      onclick: () => app.modal.close()
    }, "\u2190 \u8FD4\u56DE\u4E0A\u4E00\u6B65\u4FEE\u6539\u7528\u6237\u540D"));
  }

  // ── 视图: v0.6.3 create mode (按设计图 mobile + desktop 双适配) ────────
  renderCreateMode() {
    const calc = this.calcUnitPrice();
    const tpl = this.selectedTemplate();
    const expected = this.expectedPrice();
    const discount = this.discountInfo();
    const schoolName = this.currentSchoolName();
    return m("div", null, m("div", {
      className: "Modal-body"
    }, this.pendingStudent && m("div", {
      className: "ZS-stepper"
    }, m("div", {
      className: "ZS-stepper-dot"
    }, m("span", {
      className: "ZS-stepper-num"
    }, "1"), this.t('ziven-scheduler.forum.student-list.add-step1', '账号信息')), m("div", {
      className: "ZS-stepper-line"
    }), m("div", {
      className: "ZS-stepper-dot is-on"
    }, m("span", {
      className: "ZS-stepper-num"
    }, "2"), this.t('ziven-scheduler.forum.student-list.add-step2', '绑定卡片'))), this.loading && m("div", {
      style: "padding:30px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u4E2D..."), !this.loading && m("div", {
      className: "ZS-bindcard-form"
    }, this.renderBindError(), this.renderPendingSummary(), m("div", {
      className: "ZS-school-info"
    }, m("i", {
      className: "fas fa-house",
      "aria-hidden": "true"
    }), m("span", null, "\u5F53\u524D\u6821\u533A: ", m("strong", null, schoolName))), m("div", {
      className: "ZS-form-group"
    }, m("div", {
      className: "ZS-form-label"
    }, m("i", {
      className: "fas fa-ticket",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:6px;"
    }), "\u5361\u79CD\u6A21\u677F", m("span", {
      className: "req"
    }, "*")), this.renderTemplateDropdown()), m("div", {
      className: "ZS-form-group"
    }, m("div", {
      className: "ZS-form-label"
    }, m("i", {
      className: "fas fa-money-bill",
      style: "color:var(--zs-success-22c55e, #22c55e);margin-right:6px;"
    }), "\u91D1\u989D", m("span", {
      className: "req"
    }, "*")), m("div", {
      className: "ZS-input-inline"
    }, m("span", {
      className: "ZS-input-icon"
    }, "\xA5"), m("input", {
      type: "number",
      step: "0.01",
      min: "0",
      placeholder: "\u5B9E\u9645\u6536\u5230\u91D1\u989D",
      value: this.form.price ?? '',
      oninput: e => {
        this.form.price = e.target.value === '' ? null : parseFloat(e.target.value);
        m.redraw();
      }
    }), expected > 0 && m("span", {
      className: 'ZS-inline-tag ' + (discount ? 'diff' : '')
    }, m("span", {
      className: "ZS-inline-tag-label"
    }, "\u5E94\u6536"), m("span", {
      className: "ZS-inline-tag-value"
    }, "\xA5", expected.toFixed(0)))), discount && m("div", {
      className: "ZS-discount-warn"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:6px;"
    }), "\u4F18\u60E0 \xA5", discount.amount.toFixed(0), " (\u6298\u6263 ", discount.pct, "%)")), m("div", {
      className: "ZS-form-group"
    }, m("div", {
      className: "ZS-form-row-2col"
    }, m("div", null, m("div", {
      className: "ZS-form-label"
    }, m("i", {
      className: "fas fa-pen-to-square",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:6px;"
    }), "\u8BFE\u65F6", m("span", {
      className: "req"
    }, "*")), m("div", {
      className: "ZS-input-inline"
    }, m("input", {
      type: "number",
      min: "0",
      placeholder: "\u5B9E\u9645\u8BFE\u65F6",
      value: this.form.actual_lessons ?? '',
      oninput: e => {
        this.form.actual_lessons = e.target.value === '' ? null : parseInt(e.target.value);
        m.redraw();
      }
    }), m("span", {
      className: "ZS-inline-tag"
    }, m("span", {
      className: "ZS-inline-tag-label"
    }, "\u5361\u7247\u8BFE\u65F6"), m("span", {
      className: "ZS-inline-tag-value"
    }, this.selectedTemplate()?.lessons || 0)))), m("div", null, m("div", {
      className: "ZS-form-label"
    }, m("i", {
      className: "fas fa-gift",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:6px;"
    }), "\u8D60\u9001"), m("div", {
      className: "ZS-input-inline"
    }, m("input", {
      type: "number",
      min: "0",
      placeholder: "\u8D60\u9001\u8BFE\u65F6",
      value: this.form.bonus_lessons ?? '',
      oninput: e => {
        this.form.bonus_lessons = e.target.value === '' ? null : parseInt(e.target.value);
        m.redraw();
      }
    }), m("span", {
      className: "ZS-inline-tag"
    }, m("span", {
      className: "ZS-inline-tag-label"
    }, "\u603B\u8BFE\u65F6"), m("span", {
      className: "ZS-inline-tag-value",
      style: "color:var(--zs-success-10b981, #10b981);"
    }, (parseInt(this.form.actual_lessons) || 0) + (parseInt(this.form.bonus_lessons) || 0))))))), m("div", {
      className: "ZS-calc-result"
    }, m("div", {
      className: "ZS-calc-left"
    }, m("span", null, m("i", {
      className: "fas fa-chart-bar",
      style: "color:var(--zs-purple-8b5cf6, #8b5cf6);margin-right:6px;"
    }), "\u8BFE\u5355\u4EF7: \xA5", calc.price.toFixed(0), " \xF7 ", calc.total, " \u8BFE\u65F6")), m("div", {
      className: "ZS-calc-right"
    }, calc.unit > 0 ? `¥${calc.unit.toFixed(2)}` : '—')), m("div", {
      className: "ZS-form-group"
    }, m("div", {
      className: 'ZS-activate-row ' + (this.form.is_activated ? 'on' : '')
    }, m("div", {
      className: "ZS-activate-info"
    }, m("div", {
      className: "ZS-activate-title"
    }, m("i", {
      className: "fas fa-ticket",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:6px;"
    }), "\u7ACB\u5373\u5F00\u5361")), m("label", {
      className: "ZS-Switch"
    }, m("input", {
      type: "checkbox",
      checked: !!this.form.is_activated,
      onchange: e => {
        this.form.is_activated = e.target.checked;
        if (this.form.is_activated) {
          // 开卡: 记录激活时间
          this.form.activated_at = nowFormatted();
          // v0.18.94 (辉哥 10/25 14:36 拍板) — 过期时间**预填「今天 + 1 年」**
          //   ⚠️ 只在**空**的时候预填 ⇒ 员工自己改过的日期不许被冲掉。
          //   ⚠️ 这段**只在 create 模式跑** (extend 模式根本没有这个开关)
          //      ⇒ 已有卡的 expires_at 绝不会被「默认 1 年」冲掉。
          if (!this.form.expires_at) {
            this.form.expires_at = defaultExpiresAt();
          }
        } else {
          this.form.activated_at = null;
          // v0.6.3e: 关闭时清空过期时间 (辉哥 9/25 16:36 反馈 — 关闭时不能选过期时间)
          this.form.expires_at = null;
          if (this._flatpickrInstance) {
            this._flatpickrInstance.clear();
          }
        }
        m.redraw();
      }
    }), m("span", {
      className: "ZS-Switch-slider"
    }))), this.form.is_activated && m("div", {
      className: "ZS-expire-section"
    }, m("div", {
      className: "ZS-form-label",
      style: "margin-bottom: 10px;"
    }, m("span", null, m("i", {
      className: "fas fa-calendar-days",
      style: "color:var(--zs-warning-f59e0b, #f59e0b);margin-right:6px;"
    }), "\u8FC7\u671F\u65F6\u95F4", m("span", {
      className: "req"
    }, "*"))), m("input", {
      className: "ZS-form-input ZS-flatpickr-input",
      placeholder: "\u9009\u62E9\u8FC7\u671F\u65E5\u671F"
    }), this.form.expires_at && m("div", {
      className: "ZS-expire-hint ok"
    }, m("i", {
      className: "fas fa-check",
      style: "color:var(--zs-success-10b981, #10b981);margin-right:6px;"
    }), "\u5F00\u5361\u65F6\u95F4\u5C06\u8BB0\u5F55\u4E3A: ", m("strong", null, this.form.activated_at || nowFormatted())))), m("div", {
      className: "ZS-form-group ZS-userpicker-host"
    }, m("div", {
      className: "ZS-form-label"
    }, m("span", null, m("i", {
      className: "fas fa-user",
      style: "color:var(--zs-brand-3b82f6, #3b82f6);margin-right:6px;"
    }), "\u7ECF\u529E\u4EBA ", m("span", {
      className: "req"
    }, "*"))), this.picker.render('agent_id')))), m("div", {
      className: "Modal-footer"
    }, m("div", {
      className: "ModalFooterButtons"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: !this.canSubmit(),
      onclick: () => this.submit()
    }, this.saving ? '保存中...' : '绑定卡片'))));
  }
  content() {
    return this.mode === 'extend' ? this.renderExtendMode() : this.renderCreateMode();
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/BindCardModal', BindCardModal);